/**
 * 149-df (PROMPT 16-ACC-PAYMENT-SHADOW-P1B): Shadow Reconciliation Panel (BOD LV1).
 * Read-only. Never emits mutation calls. Displays canonical vs legacy differences,
 * per-module readiness, and overall release-gate verdict.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../lib/api";

const CATEGORY_LABELS = {
  MATCH: "Sesuai",
  UNMAPPED_LEGACY: "Legacy Belum Dipetakan",
  NEEDS_REVIEW: "Perlu Review",
  AMOUNT_MISMATCH: "Nilai Berbeda",
  STATUS_MISMATCH: "Status Berbeda",
  DUPLICATE_REFERENCE: "Nomor Duplikat",
  PROJECT_OR_CLIENT_MISMATCH: "Project/Client Tidak Cocok",
  LEGACY_FORMULA_NOT_ACTUAL_PAYMENT: "Formula Legacy (Bukan Uang Aktual)",
  INVALID_SOURCE_DATA: "Data Legacy Tidak Lengkap",
};

const MODULE_LABELS = {
  penerimaan_pembayaran: "Penerimaan Pembayaran",
  monitoring_ar: "Monitoring AR",
  collection_retention: "Collection & Retention",
  project_management_summary: "Manajemen Proyek (Ringkasan)",
  project_detail: "Detail Project",
  lifecycle_active_closing_closed: "Lifecycle Aktif/Closing/Closed",
  dashboard_bod: "Dashboard BOD",
  dashboard_fat: "Dashboard FAT",
  parent_project_maintenance: "Parent Project Maintenance",
  kalender_ar: "Kalender AR",
  export_keuangan: "Export Keuangan",
};

const STATUS_COLOR = {
  READY: "bg-emerald-100 text-emerald-800 border-emerald-300",
  NEEDS_REVIEW: "bg-amber-100 text-amber-800 border-amber-300",
  BLOCKED: "bg-rose-100 text-rose-800 border-rose-300",
};

const rupiah = (n) =>
  "Rp " + (Number(n) || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });

export default function ShadowReconciliationPage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filterCat, setFilterCat] = useState("all");

  useEffect(() => {
    setLoading(true);
    client
      .get(`/admin/shadow-report?include_records=true&max_records=500`)
      .then((r) => setData(r.data))
      .catch((e) =>
        setErr(
          e?.response?.status === 403
            ? "Halaman ini hanya untuk BOD LV1 / Super Admin."
            : e?.response?.data?.detail || "Gagal memuat laporan."
        )
      )
      .finally(() => setLoading(false));
  }, [nav]);

  const filteredRecords = useMemo(() => {
    if (!data?.records) return [];
    if (filterCat === "all") return data.records;
    return data.records.filter((r) => r.mismatch_category === filterCat);
  }, [data, filterCat]);

  if (loading)
    return (
      <div className="p-8 text-slate-500" data-testid="shadow-loading">
        Memuat laporan shadow reconciliation…
      </div>
    );
  if (err)
    return (
      <div className="p-8" data-testid="shadow-error">
        <div className="max-w-xl border border-rose-300 bg-rose-50 p-6 text-rose-800">
          {err}
        </div>
      </div>
    );
  if (!data) return null;

  const gate = data.release_gate || {};
  const summary = data.mismatch_summary || {};
  const invSum = data.invoice_reference_review_summary || {};

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="shadow-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            BOD LV1 · Read-Only
          </div>
          <h1 className="text-2xl font-bold text-slate-900">
            Shadow Reconciliation — Canonical vs Legacy
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Halaman ini membandingkan pembacaan Invoice Reference + Payment
            Receipt (canonical) dengan formula legacy. Tidak ada perubahan data,
            tidak ada aktivasi feature flag, tidak ada verifikasi otomatis.
          </p>
        </div>
        <div
          className={`px-4 py-2 rounded border font-semibold ${
            STATUS_COLOR[gate.overall] || ""
          }`}
          data-testid="shadow-overall"
        >
          {gate.overall || "—"}
        </div>
      </header>

      {/* Feature flag banner */}
      <div className="rounded border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        <strong>Feature flag Penerimaan Pembayaran:</strong>{" "}
        {data.feature_flag?.payment_receipt_entry_enabled ? "AKTIF" : "TIDAK AKTIF"}
        {data.feature_flag?.message && (
          <span className="ml-2 text-slate-500">— {data.feature_flag.message}</span>
        )}
      </div>

      {/* Invoice reference review summary */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          ["Invoice Reference Verified", invSum.verified_reference, "emerald"],
          ["Perlu Review (NEEDS_REVIEW)", invSum.needs_review, "amber"],
          ["Tanpa Nomor Accurate", invSum.missing_accurate_number, "rose"],
          [
            "Berpotensi Actual Received Palsu",
            invSum.potentially_fake_actual_received,
            "rose",
          ],
        ].map(([label, val, tone]) => (
          <div
            key={label}
            className={`rounded border border-${tone}-200 bg-${tone}-50 p-4`}
            data-testid={`shadow-kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <div className="text-xs uppercase tracking-wider text-slate-500">
              {label}
            </div>
            <div className={`text-2xl font-bold text-${tone}-800 mt-1`}>
              {val ?? 0}
            </div>
          </div>
        ))}
      </section>

      {/* Mismatch summary table */}
      <section
        className="rounded border border-slate-200 bg-white overflow-hidden"
        data-testid="shadow-mismatch-table"
      >
        <div className="px-4 py-3 bg-slate-100 font-semibold text-slate-900">
          Ringkasan per Kategori Mismatch
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-2">Kategori</th>
              <th className="text-right px-4 py-2">Jumlah</th>
              <th className="text-right px-4 py-2">Total Legacy</th>
              <th className="text-right px-4 py-2">Total Canonical</th>
              <th className="text-right px-4 py-2">Selisih</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(summary.per_category || {}).map(([cat, v]) => (
              <tr
                key={cat}
                className={v.count > 0 ? "" : "opacity-40"}
                data-testid={`shadow-row-${cat}`}
              >
                <td className="px-4 py-2 border-t border-slate-200">
                  <button
                    onClick={() => setFilterCat(cat)}
                    className="text-left hover:underline"
                  >
                    {CATEGORY_LABELS[cat]}
                  </button>
                </td>
                <td className="text-right px-4 py-2 border-t border-slate-200 font-mono">
                  {v.count}
                </td>
                <td className="text-right px-4 py-2 border-t border-slate-200 font-mono">
                  {rupiah(v.total_legacy_rp)}
                </td>
                <td className="text-right px-4 py-2 border-t border-slate-200 font-mono">
                  {rupiah(v.total_canonical_rp)}
                </td>
                <td className="text-right px-4 py-2 border-t border-slate-200 font-mono">
                  {rupiah(v.delta_rp)}
                </td>
              </tr>
            ))}
            <tr className="bg-slate-50 font-semibold">
              <td className="px-4 py-2 border-t border-slate-300">TOTAL</td>
              <td className="text-right px-4 py-2 border-t border-slate-300 font-mono">
                {summary.grand_total?.count ?? 0}
              </td>
              <td className="text-right px-4 py-2 border-t border-slate-300 font-mono">
                {rupiah(summary.grand_total?.total_legacy_rp)}
              </td>
              <td className="text-right px-4 py-2 border-t border-slate-300 font-mono">
                {rupiah(summary.grand_total?.total_canonical_rp)}
              </td>
              <td className="text-right px-4 py-2 border-t border-slate-300 font-mono">
                {rupiah(summary.grand_total?.delta_rp)}
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Module readiness */}
      <section
        className="rounded border border-slate-200 bg-white overflow-hidden"
        data-testid="shadow-modules"
      >
        <div className="px-4 py-3 bg-slate-100 font-semibold text-slate-900">
          Kesiapan Cutover per Modul
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-2">Modul</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Alasan</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(gate.modules || {}).map(([m, v]) => (
              <tr key={m} data-testid={`shadow-module-${m}`}>
                <td className="px-4 py-2 border-t border-slate-200">
                  {MODULE_LABELS[m] || m}
                </td>
                <td className="px-4 py-2 border-t border-slate-200">
                  <span
                    className={`px-2 py-1 rounded text-xs font-semibold border ${
                      STATUS_COLOR[v.status] || ""
                    }`}
                  >
                    {v.status}
                  </span>
                </td>
                <td className="px-4 py-2 border-t border-slate-200 text-slate-600">
                  {v.reason}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Records */}
      <section
        className="rounded border border-slate-200 bg-white overflow-hidden"
        data-testid="shadow-records"
      >
        <div className="px-4 py-3 bg-slate-100 flex items-center justify-between">
          <div className="font-semibold text-slate-900">
            Daftar Record ({filteredRecords.length})
          </div>
          <select
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            className="text-sm border border-slate-300 rounded px-2 py-1"
            data-testid="shadow-filter-cat"
          >
            <option value="all">Semua kategori</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Kategori</th>
                <th className="text-left px-3 py-2">Project</th>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-left px-3 py-2">Nomor Accurate</th>
                <th className="text-left px-3 py-2">Review</th>
                <th className="text-right px-3 py-2">Canonical Cash Due</th>
                <th className="text-right px-3 py-2">Actual Received</th>
                <th className="text-right px-3 py-2">Remaining</th>
                <th className="text-left px-3 py-2">Payment Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.slice(0, 200).map((r, idx) => (
                <tr
                  key={idx}
                  className="border-t border-slate-200 hover:bg-slate-50"
                  data-testid={`shadow-record-row-${idx}`}
                >
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span
                      className={`px-2 py-0.5 rounded border ${
                        r.mismatch_category === "MATCH"
                          ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                          : "bg-rose-50 text-rose-800 border-rose-200"
                      }`}
                    >
                      {CATEGORY_LABELS[r.mismatch_category] || r.mismatch_category}
                    </span>
                  </td>
                  <td className="px-3 py-2">{r.project_name || "—"}</td>
                  <td className="px-3 py-2">{r.client || "—"}</td>
                  <td className="px-3 py-2 font-mono">
                    {r.accurate_invoice_number || "—"}
                  </td>
                  <td className="px-3 py-2">{r.review_status || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {rupiah(r.canonical?.current_cash_due)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {rupiah(r.canonical?.actual_received)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {rupiah(r.canonical?.remaining_payment)}
                  </td>
                  <td className="px-3 py-2">{r.canonical?.payment_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-xs text-slate-500 pt-4">
        Sumber data: <code>GET /api/admin/shadow-report</code>. Halaman ini
        murni read-only. Preview state (11 Invoice Reference NEEDS_REVIEW, 0
        VERIFIED_REFERENCE, feature flag OFF) tidak diubah oleh pembacaan
        laporan ini.
      </p>
    </div>
  );
}
